import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { ConfirmationStatusState, withConfirmationStatusState } from '../store-features/confirmation-status-state.feature';
import {
  Address,
  AddressProducts,
  CommonMappingData,
  IBulletConfirmationStatusChangeResponse,
  IEmobCancellationDataWrapper,
  IEMobMoveOutTrackingData,
  parseAddressAsILAdresse,
  Product,
} from '@mwe/models';
import { computed, inject } from '@angular/core';
import dayjs from 'dayjs';
import { HttpClient } from '@angular/common/http';
import {
  EMOB_CANCELLATION_ADRESS_SELECTION,
  EMOB_CANCELLATION_CANCELATION,
  EMOB_CANCELLATION_ROUTE,
  ENVIRONMENT_SERVICE_TOKEN,
} from '@mwe/constants';
import { map, tap } from 'rxjs';
import { withStorageServiceFeature } from '../store-features/storage-service.feature';
import { type EnvironmentService } from '../environment/environment.service';
import { Router } from '@angular/router';
import { EnvironmentCustomerType } from '@mwe/environment';
import { formatDateDP, isProductCategoryEmobility } from '@mwe/utils';

const STORAGE_KEY = 'emob_cancellation';

const initialState = {
  addressProducts: undefined as AddressProducts,
  activityDate: undefined as string,
  cancellationDate: undefined as string,
  wantsToChooseDate: false as boolean,
  cancellationProducts: [] as Product[],
};

export type EmobCancellationStoreState = typeof initialState;

export const EmobCancellationStore = signalStore(
  { providedIn: 'root' },
  withProps(() => ({
    _httpClient: inject(HttpClient),
    _router: inject(Router),
    _environmentService: inject<EnvironmentService>(ENVIRONMENT_SERVICE_TOKEN),
  })),
  withConfirmationStatusState(),
  withStorageServiceFeature(STORAGE_KEY),
  withState<EmobCancellationStoreState>(initialState),
  withComputed(store => {
    const emobilityProducts = computed(() => {
      const products = store.addressProducts()?.products?.filter(p => isProductCategoryEmobility(p.category));
      return products?.toSorted((a, b) => a.anlageId.localeCompare(b.anlageId)) ?? [];
    });
    const address = computed(() => store.addressProducts()?.address);
    const emobCancellationDTO = computed(() => {
      const cancellationProducts = store.cancellationProducts();
      const customerMapping = new Map<
        Product['customerNumber'],
        {
          customerNumber: Product['customerNumber'];
          accountNumber: Product['accountNumber'];
          anlageIds: Set<Product['anlageId']>;
          categories: Product['category'][];
          tariffClasses: Product['tariffClasses'];
        }
      >();

      cancellationProducts?.forEach(product => {
        const existingMapping = customerMapping.get(product.customerNumber);

        if (existingMapping) {
          existingMapping.anlageIds.add(product.anlageId);
          existingMapping.tariffClasses.push(...product.tariffClasses);
          return;
        }

        customerMapping.set(product.customerNumber, {
          customerNumber: product.customerNumber,
          accountNumber: product.accountNumber,
          anlageIds: new Set([product.anlageId]),
          categories: [product.category],
          tariffClasses: product.tariffClasses,
        });
      });

      const mappingData = Array.from(customerMapping.values()).map(
        (mapping, index) =>
          ({
            orderIdx: index,
            customerNumber: mapping.customerNumber,
            accountNumber: mapping.accountNumber,
            anlageIds: Array.from(mapping.anlageIds),
            categories: mapping.categories,
            tariffClasses: mapping.tariffClasses,
          }) as unknown as CommonMappingData,
      );

      return {
        rechnungsAdresse: parseAddressAsILAdresse(address() ?? ({} as unknown as Address)),
        moveOutDate: store.cancellationDate(),
        mappingData,
      };
    });

    return {
      emobilityProducts,
      address,
      emobCancellationDTO,
    };
  }),
  withMethods(store => {
    const updateStore = (newState: Partial<typeof initialState> & Partial<ConfirmationStatusState>) => {
      const patch: Partial<typeof initialState> = { ...newState };
      // Internally the cancellationDate is always stored as a valid ISO date (YYYY-MM-DD).
      // Display formatting to DD.MM.YYYY happens only in the UI layer.
      const DISPLAY_FORMAT = 'DD.MM.YYYY';
      const STORAGE_FORMAT = 'YYYY-MM-DD';
      if ('wantsToChooseDate' in newState && !newState.wantsToChooseDate && !('moveOutDate' in newState)) {
        patch.cancellationDate = dayjs().format(STORAGE_FORMAT);
      }

      if ('cancellationDate' in newState) {
        // formatDateDP accepts both DD.MM.YYYY (from the date picker) and ISO input and normalises to YYYY-MM-DD.
        patch.cancellationDate = formatDateDP(newState.cancellationDate, DISPLAY_FORMAT);
      }

      // Ensure cancellationProducts is sorted by anlageId.
      if ('cancellationProducts' in newState) {
        patch.cancellationProducts = patch.cancellationProducts?.toSorted((a, b) => a.anlageId.localeCompare(b.anlageId)) ?? [];
      }

      patchState(store, patch);
    };
    const initEmobCancellationProcess = (addressProducts: AddressProducts) => {
      updateStore({ addressProducts });

      // only navigate to second step when address has already been pre-selected
      const url = addressProducts ? EMOB_CANCELLATION_CANCELATION : EMOB_CANCELLATION_ADRESS_SELECTION;
      store._router.navigateByUrl(`/${EMOB_CANCELLATION_ROUTE}/${url}`);
    };
    const confirmEmobCancellation = () => {
      const ressourceUrl = `${store._environmentService.getApiUrl()}api/emobility/cancellation/confirm`;
      return store._httpClient.post<IBulletConfirmationStatusChangeResponse>(ressourceUrl, store.emobCancellationDTO()).pipe(
        tap(res => updateStore({ submitId: res.submitId })),
        map(res => res),
      );
    };
    const confirmStatusEmobCancellation = (submitId: number) => {
      const ressourceUrl = `${store._environmentService.getApiUrl()}api/emobility/cancellation/confirmStatus?submitId=${submitId}`;
      return store._httpClient.get<IBulletConfirmationStatusChangeResponse>(ressourceUrl);
    };
    const getEmobCancellationStatusData = (submitId: number) => {
      const ressourceUrl = `${store._environmentService.getApiUrl()}api/emobility/cancellation/orderStatusDetails/${submitId}`;
      return store._httpClient.get<IEmobCancellationDataWrapper>(ressourceUrl);
    };
    const getFeedbackLink = (isSuccess = false) => {
      const isB2C = store._environmentService.getPortalCustomerType() === EnvironmentCustomerType.PRIVAT;
      const isB2B = store._environmentService.getPortalCustomerType() === EnvironmentCustomerType.BUSINESS;

      if (isB2C) {
        if (isSuccess) return 'https://netigate.se/ra/s.aspx?s=1309859X550412385X76454';
        return 'https://netigate.se/ra/s.aspx?s=1309860X550412599X95450';
      }

      if (isB2B) {
        if (isSuccess) return 'https://netigate.se/ra/s.aspx?s=1309857X550411814X40385';
        return 'https://netigate.se/ra/s.aspx?s=1309858X550412088X54530';
      }

      return undefined;
    };
    const getTrackingData = (): IEMobMoveOutTrackingData => {
      const cancellationProducts = store?.cancellationProducts() || store?.addressProducts()?.products;
      const trackingProducts = cancellationProducts?.map(p => {
        return {
          productType: 'emobility',
          tarifNameUrl: p.tariffClasses[0],
          tarifKeyUrl: p.tarifKey || p.tariffClasses[0],
        };
      });

      if (trackingProducts?.length === 0) {
        trackingProducts.push({
          productType: 'emobility',
          tarifNameUrl: 'unknown',
          tarifKeyUrl: 'unknown',
        });
      }

      return {
        event: 'abmeldung',
        url: window.location.pathname,
        cancellationAction: 'false',
        dateSelection: store?.wantsToChooseDate() ? 'wunschtermin' : 'naechstmoeglich',
        products: trackingProducts,
      } as IEMobMoveOutTrackingData;
    };
    return {
      updateStore,
      initEmobCancellationProcess,
      confirmEmobCancellation,
      confirmStatusEmobCancellation,
      getEmobCancellationStatusData,
      getFeedbackLink,
      getTrackingData,
    };
  }),
);
